import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RENDER } from '../config';

export type RenderStack = {
  renderer: THREE.WebGLRenderer;
  composer: EffectComposer;
  bloom: UnrealBloomPass;
  resize: (camera: THREE.PerspectiveCamera) => void;
};

export function createRenderStack(canvas: HTMLCanvasElement, scene: THREE.Scene, camera: THREE.Camera): RenderStack {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
    logarithmicDepthBuffer: true,
    preserveDrawingBuffer: true, // screenshots in playtests
  });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = RENDER.exposure;
  renderer.info.autoReset = false; // counted per frame by the game (composer renders several passes)

  const target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, samples: 4 });
  const composer = new EffectComposer(renderer, target);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), RENDER.bloomStrength, RENDER.bloomRadius, RENDER.bloomThreshold);
  composer.addPass(bloom);
  composer.addPass(new OutputPass());

  const resize = (cam: THREE.PerspectiveCamera) => {
    const w = Math.max(1, canvas.clientWidth);
    const h = Math.max(1, canvas.clientHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, RENDER.maxDpr);
    const bw = Math.floor(w * dpr), bh = Math.floor(h * dpr);
    if (canvas.width === bw && canvas.height === bh) return;
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h, false);
    composer.setPixelRatio(dpr);
    composer.setSize(w, h);
    cam.aspect = w / h;
    cam.updateProjectionMatrix();
  };

  return { renderer, composer, bloom, resize };
}
